import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { PerformanceTab, QuestionnairePerformance, ChartDataPoint, ChartSeries, PieChartStoreData, QueryMode } from '../types'
import { PerformanceResults } from './PerformanceResults'
import { ProcessStatusQueryParams } from '../types'
import { useStorePerformanceContext } from '../context/StorePerformanceContext'

interface QueryResultsProps {
  hasQueried: boolean
  resultsLoading: boolean
  activeTab: PerformanceTab
  onTabChange: (tab: PerformanceTab) => void
  selectedResultIds: string[]
  toggleResultItem: (id: string) => void
  selectAllResultItems: () => void
  searchResult: string
  setSearchResult: (value: string) => void
  paginatedResultItems: QuestionnairePerformance[]
  filteredResultItems: QuestionnairePerformance[]
  performanceData: QuestionnairePerformance[]
  resultsPage: number
  setResultsPage: (page: number) => void
  totalPages: number
  resultListTitle: string
  chartType: 'bar' | 'line' | 'pie'
  onChartTypeChange: (type: 'bar' | 'line' | 'pie') => void
  chartData: ChartDataPoint[]
  chartSeries: ChartSeries[]
  globalAverageScore: number | null
  chartColors: (index: number) => string
  pieChartData: PieChartStoreData[]
  pieChartLoading: boolean
  pieChartPage: number
  pieChartTotalPages: number
  onPieChartPageChange: (page: number) => void
  queryMode: QueryMode
  processStatusQueryParams: ProcessStatusQueryParams
  scoreMap: Map<string, number>
  onRangeApplied?: (range: [number, number]) => void
}

const QueryResults = ({
  hasQueried,
  resultsLoading,
  activeTab,
  onTabChange,
  selectedResultIds,
  toggleResultItem,
  selectAllResultItems,
  searchResult,
  setSearchResult,
  paginatedResultItems,
  filteredResultItems,
  performanceData,
  resultsPage,
  setResultsPage,
  totalPages,
  resultListTitle,
  chartType,
  onChartTypeChange,
  chartData,
  chartSeries,
  globalAverageScore,
  chartColors,
  pieChartData,
  pieChartLoading,
  pieChartPage,
  pieChartTotalPages,
  onPieChartPageChange,
  queryMode,
  processStatusQueryParams,
  scoreMap,
  onRangeApplied,
}: QueryResultsProps) => {
  const { styles, colors, icons, t } = useStorePerformanceContext()

  const EmptyState = () => (
    <Box sx={styles.section}>
      <Box sx={styles.emptyState}>
        <Typography sx={styles.emptyTitle}>
          {t('dashboard.storePerformance.emptyState.title')}
        </Typography>
        <Typography sx={styles.emptyDescription}>
          <Trans
            i18nKey="dashboard.storePerformance.emptyState.description"
            components={{
              bold: <Box component="span" sx={{ fontWeight: 'bold' }} />
            }}
          />
        </Typography>
      </Box>
    </Box>
  )

  if (resultsLoading) {
    return (
      <Box sx={styles.section}>
        <Box sx={styles.emptyState}>
          <Typography sx={styles.emptyTitle}>
            {t('general.loading')}
          </Typography>
          <Typography sx={styles.emptyDescription}>
            {t('dashboard.storePerformance.loadingResults')}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!hasQueried || performanceData.length === 0) {
    return <EmptyState />
  }

  return (
      <PerformanceResults
        activeTab={activeTab}
        onTabChange={onTabChange}
        selectedResultIds={selectedResultIds}
        toggleResultItem={toggleResultItem}
        selectAllResultItems={selectAllResultItems}
        searchResult={searchResult}
        setSearchResult={setSearchResult}
        paginatedResultItems={paginatedResultItems}
        filteredResultItems={filteredResultItems}
        resultsPage={resultsPage}
        setResultsPage={setResultsPage}
        totalPages={totalPages}
        resultListTitle={resultListTitle}
        styles={styles}
        colors={colors}
        icons={icons}
        t={t}
        chartType={chartType}
        onChartTypeChange={onChartTypeChange}
        chartData={chartData}
        chartSeries={chartSeries}
        globalAverageScore={globalAverageScore}
        chartColors={chartColors}
        pieChartData={pieChartData}
        pieChartLoading={pieChartLoading}
        pieChartPage={pieChartPage}
        pieChartTotalPages={pieChartTotalPages}
        onPieChartPageChange={onPieChartPageChange}
        queryMode={queryMode}
        processStatusQueryParams={processStatusQueryParams}
        scoreMap={scoreMap}
        hasQueried={hasQueried}
        onRangeApplied={onRangeApplied}
      />
  )
}

export { QueryResults }
